import { useEffect, useRef, useState } from "react";
import "App.sass";
import TrashIcon from "Assets/TrashIcon";

import PlantingModal from "./PlantingModal";
import DeletePlantingModal from "./DeletePlantingModal";
import { usePlantStore } from "Store/PlantStore";
import { useVarietyStore } from "Store/VarietyStore";
import { getPlantDisplayName } from "Helpers/PlantName";

import type PlantingModel from "Models/Planting";
import type { Dispatch, SetStateAction } from "react";

const PlantingCard = ({
  index,
  planting,
  multiSelectMode = false,
  selectedPlantings = [],
  setSelectedPlantings,
}: {
  planting?: PlantingModel;
  index: number;
  multiSelectMode?: boolean;
  selectedPlantings?: string[];
  setSelectedPlantings?: Dispatch<SetStateAction<string[]>>;
}) => {
  const plants = usePlantStore((state) => state.plants);
  const plant = plants.find((item) => item._id === planting?.plantId);
  const varieties = useVarietyStore((state) => state.varieties);
  const elementRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [displayDeleteButton, setDisplayDeleteButton] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPlantingInfoModal, setShowPlantingInfoModal] = useState(false);
  const [showPlantCreateModal, setShowPlantingCreateModal] = useState(false);
  const [showPlantSelectModal, setShowPlantingSelectModal] = useState(false);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (elementRef.current) {
        setWidth(elementRef.current.clientWidth);
      }
    });

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const marginLeft = () => (width ? `${width / 2}px` : "0px");
  const isSelected =
    planting?._id !== undefined && selectedPlantings.includes(planting._id);

  const hideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <div
      ref={elementRef}
      className="relative"
      onMouseEnter={() => {
        if (planting?._id) {
          setDisplayDeleteButton(true);
        }
      }}
      onMouseLeave={() => {
        if (planting?._id) {
          setDisplayDeleteButton(false);
        }
      }}
    >
      {displayDeleteButton && !multiSelectMode && (
        <button
          onClick={() => {
            setShowDeleteModal(true);
            setDisplayDeleteButton(false);
          }}
          className="btn p-15 text-[red] bg-[red] circle absolute"
          style={{ marginLeft: marginLeft() }}
        >
          <TrashIcon />
        </button>
      )}

      <button
        key={index}
        onClick={() => {
          setDisplayDeleteButton(false);
          if (planting?._id) {
            if (multiSelectMode) {
              if (!setSelectedPlantings) return;
              const newPlantings = isSelected
                ? selectedPlantings.filter((id) => id !== planting._id)
                : [...selectedPlantings, planting._id];
              setSelectedPlantings(newPlantings);
              return;
            }
            setShowPlantingInfoModal(true);
            return;
          }
          setShowPlantingCreateModal(true);
        }}
        className="grid-button bg-green text-lg"
      >
        {multiSelectMode && planting?._id && (
          <input
            type="checkbox"
            aria-label={`Select ${getPlantDisplayName(plant, varieties) || "planting"}`}
            checked={isSelected}
            onChange={() => {
              if (!setSelectedPlantings || !planting?._id) return;
              const newPlantings = isSelected
                ? selectedPlantings.filter((id) => id !== planting._id)
                : [...selectedPlantings, planting._id];
              setSelectedPlantings(newPlantings);
            }}
            className="w-5 h-5"
          />
        )}
        {!planting?._id
          ? "+"
          : getPlantDisplayName(plant, varieties) || "Unknown Plant"}
      </button>

      <DeletePlantingModal
        hideDeleteModal={hideDeleteModal}
        showDeleteModal={showDeleteModal}
        plantingId={planting?._id}
        index={index}
      />

      <PlantingModal
        showPlantingInfoModal={showPlantingInfoModal}
        showPlantSelectModal={showPlantSelectModal}
        showCreateModal={showPlantCreateModal}
        setShowCreateModal={setShowPlantingCreateModal}
        setShowPlantingInfoModal={setShowPlantingInfoModal}
        setShowPlantSelectModal={setShowPlantingSelectModal}
        index={index}
      />
    </div>
  );
};

export default PlantingCard;
